import React from 'react';

/**
 * Error Boundary component to catch and handle React errors gracefully
 * Provides user-friendly error messages and fallback UI
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Report error to monitoring service (if available)
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false
      });
    }
  }

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: prevState.retryCount + 1
    }));
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback, showDetails = false } = this.props;
      
      // Use custom fallback if provided
      if (Fallback) {
        return (
          <Fallback
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onRetry={this.handleRetry}
            onReload={this.handleReload}
          />
        );
      }

      // Default error UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
                <line x1="15" y1="9" x2="9" y2="15" stroke="#ef4444" strokeWidth="2"/>
                <line x1="9" y1="9" x2="15" y2="15" stroke="#ef4444" strokeWidth="2"/>
              </svg>
            </div>
            
            <h2 className="error-title">Oops! Something went wrong</h2>
            
            <p className="error-message">
              We're sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.
            </p>
            
            <div className="error-actions">
              <button 
                className="retry-button"
                onClick={this.handleRetry}
                disabled={this.state.retryCount >= 3}
              >
                {this.state.retryCount >= 3 ? 'Max retries reached' : 'Try Again'}
              </button>
              
              <button 
                className="reload-button"
                onClick={this.handleReload}
              >
                Reload Page
              </button>
            </div>
            
            {showDetails && this.state.error && (
              <details className="error-details">
                <summary>Technical Details</summary>
                <div className="error-stack">
                  <h4>Error:</h4>
                  <pre>{this.state.error.toString()}</pre>
                  
                  {this.state.errorInfo && (
                    <>
                      <h4>Component Stack:</h4>
                      <pre>{this.state.errorInfo.componentStack}</pre>
                    </>
                  )}
                </div>
              </details>
            )}
          </div>
          
          <style jsx>{`
            .error-boundary {
              min-height: 400px;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 2rem;
              background: #fafafa;
              border-radius: 8px;
              margin: 1rem;
            }
            
            .error-boundary-content {
              text-align: center;
              max-width: 500px;
            }
            
            .error-icon {
              margin-bottom: 1.5rem;
              display: flex;
              justify-content: center;
            }
            
            .error-title {
              color: #1f2937;
              font-size: 1.5rem;
              font-weight: 600;
              margin-bottom: 1rem;
            }
            
            .error-message {
              color: #6b7280;
              font-size: 1rem;
              line-height: 1.6;
              margin-bottom: 2rem;
            }
            
            .error-actions {
              display: flex;
              gap: 1rem;
              justify-content: center;
              margin-bottom: 2rem;
            }
            
            .retry-button,
            .reload-button {
              padding: 0.75rem 1.5rem;
              border: none;
              border-radius: 6px;
              font-size: 0.875rem;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s ease;
            }
            
            .retry-button {
              background: #3b82f6;
              color: white;
            }
            
            .retry-button:hover:not(:disabled) {
              background: #2563eb;
            }
            
            .retry-button:disabled {
              background: #9ca3af;
              cursor: not-allowed;
            }
            
            .reload-button {
              background: #f3f4f6;
              color: #374151;
              border: 1px solid #d1d5db;
            }
            
            .reload-button:hover {
              background: #e5e7eb;
            }
            
            .error-details {
              text-align: left;
              margin-top: 2rem;
              padding: 1rem;
              background: #f9fafb;
              border: 1px solid #e5e7eb;
              border-radius: 6px;
            }
            
            .error-details summary {
              cursor: pointer;
              font-weight: 500;
              color: #374151;
              margin-bottom: 1rem;
            }
            
            .error-stack h4 {
              color: #374151;
              font-size: 0.875rem;
              font-weight: 600;
              margin: 1rem 0 0.5rem 0;
            }
            
            .error-stack pre {
              background: #1f2937;
              color: #f9fafb;
              padding: 1rem;
              border-radius: 4px;
              font-size: 0.75rem;
              overflow-x: auto;
              white-space: pre-wrap;
              word-break: break-word;
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for wrapping components with error boundary
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

// Hook for error handling in functional components
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);
  
  const resetError = React.useCallback(() => {
    setError(null);
  }, []);
  
  const handleError = React.useCallback((error) => {
    console.error('Error caught by useErrorHandler:', error);
    setError(error);
  }, []);
  
  // Throw error to be caught by ErrorBoundary
  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);
  
  return { handleError, resetError };
};

// Network error component
export const NetworkErrorFallback = ({ onRetry, onReload }) => (
  <div className="network-error">
    <div className="network-error-content">
      <div className="network-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 17L12 22L22 17" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      
      <h3>Connection Problem</h3>
      <p>Unable to connect to the server. Please check your internet connection and try again.</p>
      
      <div className="network-actions">
        <button onClick={onRetry} className="retry-button">
          Try Again
        </button>
        <button onClick={onReload} className="reload-button">
          Reload Page
        </button>
      </div>
    </div>
    
    <style jsx>{`
      .network-error {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 300px;
        padding: 2rem;
      }
      
      .network-error-content {
        text-align: center;
        max-width: 400px;
      }
      
      .network-icon {
        margin-bottom: 1rem;
        display: flex;
        justify-content: center;
      }
      
      .network-error h3 {
        color: #1f2937;
        margin-bottom: 0.5rem;
      }
      
      .network-error p {
        color: #6b7280;
        margin-bottom: 1.5rem;
      }
      
      .network-actions {
        display: flex;
        gap: 1rem;
        justify-content: center;
      }
      
      .retry-button,
      .reload-button {
        padding: 0.5rem 1rem;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 0.875rem;
      }
      
      .retry-button {
        background: #f59e0b;
        color: white;
      }
      
      .reload-button {
        background: #f3f4f6;
        color: #374151;
        border: 1px solid #d1d5db;
      }
    `}</style>
  </div>
);

export default ErrorBoundary;